// The 對齊地圖 overlay as MapLibre sources and layers: the site outline (a
// crisp line, no tint), its rotate and resize handles, and the placed
// stations and 防護區 as reference points. Pure: data in, GeoJSON out.
import { toLatLng } from "../../../../utils/geo";
import { EDIT_COLOR } from "./constants";
import { getStationColor } from "./stationUtils";
import { floorCorners, handleSite, resizeHandles } from "./geoAlign";

export const lngLatOf = ([lat, lng]) => [lng, lat];
export const EMPTY = { type: "FeatureCollection", features: [] };
const feature = (geometry, properties = {}) => ({
  type: "Feature",
  properties,
  geometry,
});
const collection = (features) => ({ type: "FeatureCollection", features });

const ZONE_SEGMENTS = 48;
const ZONE_COLOR = getStationColor("防護區").primary;
const CASING = "#ffffff";

const ring = (latLngs) => [...latLngs, latLngs[0]].map(lngLatOf);

const isZone = (s) => s.category === "防護區" && s.radius_meters > 0;

const zoneRing = (ref, { position_meters: { x, y }, radius_meters: r }) =>
  ring(
    Array.from({ length: ZONE_SEGMENTS }, (_, i) => {
      const a = (2 * Math.PI * i) / ZONE_SEGMENTS;
      return toLatLng(ref, x + r * Math.cos(a), y + r * Math.sin(a));
    }),
  );

/** GeoJSON for every overlay source, or empty collections without a ref. */
export const overlayData = ({ ref, floor, stations = [] }) => {
  const placed = stations.filter((s) => s.position_meters);
  const handle = ref && toLatLng(ref, ...handleSite(floor));
  return {
    area: ref
      ? collection([
          feature({
            type: "Polygon",
            coordinates: [ring(floorCorners(floor, ref))],
          }),
        ])
      : EMPTY,
    handle: ref
      ? collection([
          feature({
            type: "LineString",
            coordinates: [
              toLatLng(ref, floor.width / 2, floor.height),
              handle,
            ].map(lngLatOf),
          }),
          feature({ type: "Point", coordinates: lngLatOf(handle) }),
        ])
      : EMPTY,
    stations: ref
      ? collection(
          placed.map((s) =>
            feature(
              {
                type: "Point",
                coordinates: lngLatOf(
                  toLatLng(ref, s.position_meters.x, s.position_meters.y),
                ),
              },
              { color: getStationColor(s.category).primary },
            ),
          ),
        )
      : EMPTY,
    zones: ref
      ? collection(
          placed
            .filter(isZone)
            .map((s) =>
              feature({ type: "Polygon", coordinates: [zoneRing(ref, s)] }),
            ),
        )
      : EMPTY,
    resize: ref
      ? collection(
          resizeHandles(floor).map((h) =>
            feature(
              {
                type: "Point",
                coordinates: lngLatOf(toLatLng(ref, ...h.site)),
              },
              { kind: h.kind },
            ),
          ),
        )
      : EMPTY,
  };
};

export const SOURCES = ["zones", "area", "handle", "resize", "stations"];
// The plan image goes under this layer, so every reference stays on top.
export const IMAGE_BELOW = "zone-line";

export const addOverlayLayers = (map) => {
  SOURCES.forEach((id) => map.addSource(id, { type: "geojson", data: EMPTY }));
  [
    {
      id: "zone-line",
      type: "line",
      source: "zones",
      paint: {
        "line-color": ZONE_COLOR,
        "line-width": 1.5,
        "line-dasharray": [2, 1.5],
      },
    },
    {
      id: "area-casing",
      type: "line",
      source: "area",
      paint: { "line-color": CASING, "line-width": 4, "line-opacity": 0.9 },
    },
    {
      id: "area-line",
      type: "line",
      source: "area",
      paint: { "line-color": EDIT_COLOR, "line-width": 2 },
    },
    {
      id: "handle-line",
      type: "line",
      source: "handle",
      filter: ["==", "$type", "LineString"],
      paint: { "line-color": EDIT_COLOR, "line-width": 2 },
    },
    {
      id: "handle-dot",
      type: "circle",
      source: "handle",
      filter: ["==", "$type", "Point"],
      paint: {
        "circle-radius": 8,
        "circle-color": CASING,
        "circle-stroke-color": EDIT_COLOR,
        "circle-stroke-width": 3,
      },
    },
    {
      id: "resize-dot",
      type: "circle",
      source: "resize",
      paint: {
        "circle-radius": 5,
        "circle-color": CASING,
        "circle-stroke-color": EDIT_COLOR,
        "circle-stroke-width": 2,
      },
    },
    {
      id: "station-dot",
      type: "circle",
      source: "stations",
      paint: {
        "circle-radius": 4,
        "circle-color": ["get", "color"],
        "circle-stroke-color": CASING,
        "circle-stroke-width": 1.5,
      },
    },
  ].forEach((layer) => map.addLayer(layer));
};

/**
 * setStyle transform for a 正射影像/電子地圖 swap: the new base raster, with
 * every overlay source and layer (and the plan image) carried over.
 */
export const keepOverlays = (prev, next) => {
  const isBase = (id) => prev.sources[id]?.type === "raster";
  const kept = Object.fromEntries(
    Object.entries(prev.sources).filter(([id]) => !isBase(id)),
  );
  return {
    ...next,
    sources: { ...next.sources, ...kept },
    layers: [...next.layers, ...prev.layers.filter((l) => !isBase(l.source))],
  };
};
